import { Boxes, RefreshCw, Trash2, Upload } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { api } from '@/lib/api';
import { getSocket } from '@/lib/socket';
import { prettyModelName } from '@/lib/utils';
import type { ModelFile } from '@/lib/types';
import { ModelThumbnail } from '../sequences/ModelThumbnail';

/**
 * Inventory management for GLB models. Upload via file picker or
 * drag-and-drop; delete individual models (server refuses if a
 * sequence still references the model, unless the user confirms).
 */
export function ModelsPage() {
  const [models, setModels] = useState<ModelFile[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const load = async () => {
    setRefreshing(true);
    try {
      const mdls = await api.listModels();
      setModels((mdls ?? []).filter((m) => m.name.toLowerCase().endsWith('.glb')));
    } catch (err) {
      toast.error('Failed to load models', {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    let stop: (() => void) | undefined;
    getSocket().then((s) => {
      const handler = () => load();
      s.on('modelListChanged', handler);
      stop = () => s.off('modelListChanged', handler);
    });
    return () => stop?.();
  }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return models.filter((m) => !q || m.name.toLowerCase().includes(q));
  }, [models, filter]);

  const uploadFiles = async (files: File[]) => {
    const glbs = files.filter((f) => /\.glb$/i.test(f.name));
    const rejected = files.length - glbs.length;
    if (rejected > 0) {
      toast.warning(`${rejected} non-GLB file${rejected === 1 ? '' : 's'} skipped`);
    }
    if (glbs.length === 0) return;

    setUploading(true);
    try {
      const result = await api.uploadModels(glbs);
      toast.success(`Uploaded ${result.uploaded.length} model${result.uploaded.length === 1 ? '' : 's'}`);
      await load();
    } catch (err) {
      toast.error('Upload failed', {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setUploading(false);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length) void uploadFiles(files);
    // Reset so selecting the same file again re-fires change
    e.target.value = '';
  };

  const deleteModel = async (m: ModelFile) => {
    try {
      await api.deleteModel(m.name);
      toast.success(`Deleted ${prettyModelName(m.path)}`);
      await load();
    } catch (err) {
      if ((err as { code?: string })?.code === 'IN_USE') {
        const usedBy = (err as { usedBy?: string[] }).usedBy ?? [];
        const ok = confirm(
          `"${prettyModelName(m.path)}" is used by ${usedBy.length} sequence${
            usedBy.length === 1 ? '' : 's'
          }:\n\n${usedBy.map((u) => `  • ${u}`).join('\n')}\n\nDelete anyway? Sequences will keep the reference and clients that try to load it will fail.`,
        );
        if (!ok) return;
        try {
          await api.deleteModel(m.name, { force: true });
          toast.success(`Deleted ${prettyModelName(m.path)} (forced)`);
          await load();
        } catch (err2) {
          toast.error('Delete failed', {
            description: err2 instanceof Error ? err2.message : String(err2),
          });
        }
      } else {
        toast.error('Delete failed', {
          description: err instanceof Error ? err.message : String(err),
        });
      }
    }
  };

  return (
    <Card
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const files = Array.from(e.dataTransfer.files ?? []);
        if (files.length) void uploadFiles(files);
      }}
      className={dragOver ? 'ring-2 ring-primary/60' : undefined}
    >
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Models</CardTitle>
          <CardDescription>
            {filter
              ? `${filtered.length} of ${models.length} GLB${models.length === 1 ? '' : 's'}`
              : `${models.length} GLB${models.length === 1 ? '' : 's'} on the sync server.`}
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Filter…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-40"
          />
          <Button
            variant="ghost"
            size="icon"
            aria-label="Refresh"
            onClick={load}
            disabled={refreshing}
          >
            <RefreshCw className={refreshing ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".glb,model/gltf-binary"
            multiple
            className="hidden"
            onChange={handleFileInput}
          />
          <Button disabled={uploading} onClick={() => fileInputRef.current?.click()}>
            <Upload className="h-4 w-4" />
            {uploading ? 'Uploading…' : 'Upload GLB'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {models.length === 0 ? (
          <div className="grid place-items-center gap-3 rounded-md border border-dashed py-14 text-center">
            <Boxes className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No models yet. Drop `.glb` files here, or click Upload GLB.
            </p>
          </div>
        ) : (
          <div
            ref={setScrollRoot}
            className="grid max-h-[calc(100vh-16rem)] grid-cols-2 gap-3 overflow-y-auto rounded-md border p-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
          >
            {filtered.map((m) => (
              <div
                key={m.path}
                className="group relative flex flex-col overflow-hidden rounded-md border bg-card"
                title={prettyModelName(m.path)}
              >
                <ModelThumbnail
                  modelPath={m.path}
                  name={m.name}
                  size="md"
                  scrollRoot={scrollRoot}
                />
                <div className="flex items-center gap-1 border-t bg-muted/30 px-2 py-1.5">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-medium">
                      {prettyModelName(m.path)}
                    </div>
                    <div className="truncate text-[10px] text-muted-foreground">
                      {m.name}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete ${m.name}`}
                    onClick={() => void deleteModel(m)}
                    className="h-7 w-7"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="col-span-full grid place-items-center py-8 text-xs text-muted-foreground">
                No models match.
              </div>
            )}
          </div>
        )}
        {dragOver && (
          <div className="pointer-events-none absolute inset-0 grid place-items-center rounded-md bg-primary/10 text-sm font-medium text-primary">
            Drop .glb files to upload
          </div>
        )}
      </CardContent>
    </Card>
  );
}
